import { useErrorHandlingMutation } from '@backoffice-frontend/data-fetching'
import { FccAdAlertType } from '@backoffice-frontend/graphql'
import { useTimers } from '@backoffice-frontend/hooks'
import { useMachine } from '@xstate/react'
import { useMeetingManager } from 'amazon-chime-sdk-component-library-react'
import { MeetingSessionConfiguration } from 'amazon-chime-sdk-js'
import { useEffect } from 'react'
import useSound from 'use-sound'
import { setup } from 'xstate'
import {
  type CallControlsVehicleFragment,
  useFccJoinPassengerCallMutation,
} from './CallControls.hook'

const callMachine = setup({
  types: {
    events: {} as
      | { type: 'PICK_UP' }
      | { type: 'START_CALL' }
      | { type: 'MUTE' }
      | { type: 'UNMUTE' }
      | { type: 'HANG_UP' }
      | { type: 'END_CALL' },
  },
  actions: {
    // Please provide implementations
    joinCall: () => {},
    mute: () => {},
    unmute: () => {},
    leaveCall: () => {},
  },
}).createMachine({
  /** @xstate-layout N4IgpgJg5mDOIC5QAoC2BDAxgCwJYDswBKAOgCcCpKBiABQEkBhAaQH0BVWgbQAYBdRKAAOAe1i4ALrhH5BIAB6IALAHYlJAKwBmAEzaVWgIw6tWpQA4NAGhABPRIY0A2Ekt06V5tWaeGlSgF8AmzQsPEJSACsRAhoAZQAVAEEAJQTWRiSAGSzeASQQUXEpGTlFBCUNcxIATnMajQ0VQ18nNx0bewQtFRrajx4dJ3NWp2cgkIwcAmISAloyESgyOFhqAAkkgDkAcQ5ufjkiyWlZAvKlJz7zCxqecx4awzVDQ07EFQ1DV1elHkMRi0xk4JiBQtMInN8AslitYLASABXfCoRESSDUACy7ASAFE8kcxCdSudlA0SDwtG1DPcak5KW53ghfFoSCodDweEpns0tBo9KDweFZvNFstViRUeiINR2FtsXiCQVjiUzqALuZWTUVJ5nn56WYtEznC4+U4DKMajUhoKpsLSAAbMDoABuNFxWwAIhlsrlDsqiaqyspzWyPJqanylA16kyav5NJqzE0NNbPOYgsEQPgRBA4HIhTMiITiqdgwhnjww54hrpTDd7kyALQuLnDLzW3Q8fmGGq2sJFuYQJ0l4lqhRknQkEY3JzDdlaSxKJk0vQUns8XpUypefsQ2YUfBUI+joOkirmKcczWGUx-RfmN52RCP1zaV4clTm+OePf2kjRLEJ4BqWJLqso0YkDS5rRl8ag6NaTI6JUbL+L0QJzphfZZoWkKirCqynmW546AC1aXk4daLn85hMqoprai0lJpqmGh-oO+HivCSIomikBEWBE4IEMLjsjWlEmNRT5dM4rL1M0BosQ07F4dCYpwgiUr8SBY7lmMKhsmaNyOH8nLWM+CCydOjGKR4rEqbMTqupQAnjhcbiGc8XwaIMahKB0FmXqyNLclo2rdmYkaZgEQA */
  initial: 'ringing',
  states: {
    idle: {},
    ringing: {
      on: { PICK_UP: 'joining' },
    },
    joining: {
      entry: 'joinCall',
      on: { START_CALL: 'inProgress' },
    },
    inProgress: {
      initial: 'unmuted',
      states: {
        unmuted: {
          entry: 'unmute',
          on: { MUTE: 'muted' },
        },
        muted: {
          entry: 'mute',
          on: { UNMUTE: 'unmuted' },
        },
      },
      on: { HANG_UP: 'leaving' },
    },
    leaving: {
      entry: 'leaveCall',
      on: { END_CALL: 'idle' },
    },
  },
})

export const useCall = ({
  vehicle,
}: { vehicle: CallControlsVehicleFragment }) => {
  const { setTimer } = useTimers()
  const meetingManager = useMeetingManager()
  const meetingSession = meetingManager.meetingSession
  const incomingPassengerCallAlert = vehicle.fccCurrentAdAlerts?.alerts.find(
    alert => alert.type === FccAdAlertType.IncomingPassengerCall,
  )
  // const hasIncomingPassengerCallAlert = !!incomingPassengerCallAlert
  const callStartedAt = incomingPassengerCallAlert
    ? new Date(incomingPassengerCallAlert.createdAt)
    : undefined
  const [fccJoinPassengerCallMutation] = useErrorHandlingMutation(
    useFccJoinPassengerCallMutation,
  )

  const [state, send] = useMachine(
    callMachine.provide({
      actions: {
        joinCall: async () => {
          await fccJoinPassengerCallMutation({
            variables: { vehicleId: vehicle.id },
            onCompleted: async data => {
              const { meeting, attendee } = JSON.parse(
                data.fccJoinPassengerCall,
              )
              const meetingSessionConfiguration =
                new MeetingSessionConfiguration(
                  { Meeting: meeting },
                  { Attendee: attendee },
                )
              await meetingManager.join(meetingSessionConfiguration)
              await meetingManager.start()
            },
          })
        },
        mute: () => {
          meetingSession?.audioVideo.realtimeMuteLocalAudio()
        },
        unmute: () => {
          meetingSession?.audioVideo.realtimeUnmuteLocalAudio()
        },
        leaveCall: async () => {
          setTimer(
            'end-call',
            async () => {
              meetingSession?.audioVideo.stop()
              await meetingManager.leave()
            },
            1500,
          )
        },
      },
    }),
  )

  // Play sound for incoming passenger calls
  const [play, { stop }] = useSound('/assets/sounds/incoming-call.mp3', {
    volume: 1,
    loop: true,
  })
  useEffect(() => {
    if (state.matches('ringing')) {
      play()
    } else {
      stop()
    }
  }, [state, play, stop])

  useEffect(() => {
    const attendeeId = meetingSession?.configuration.credentials?.attendeeId
    if (!attendeeId) {
      return
    }
    meetingSession.audioVideo.addObserver({
      audioVideoDidStart: () => send({ type: 'START_CALL' }),
      audioVideoDidStop: () => send({ type: 'END_CALL' }),
    })
  }, [meetingSession, send])

  return {
    pickup: () => send({ type: 'PICK_UP' }),
    hangup: () => send({ type: 'HANG_UP' }),
    mute: () => send({ type: 'MUTE' }),
    unmute: () => send({ type: 'UNMUTE' }),
    state,
    callStartedAt,
  }
}
